import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { LoadingState } from "@repo/app-types/client/client";
import {
  IntegrationProvider,
  type Integration,
} from "@repo/app-types/entities/integration";
import type { Selector } from "react-redux";
import { getAxiosInstanceWithAuth } from "../../api/axiosConfig.js";
import type { RootState } from "../../app/store.js";

export const integrationsAdapter = createEntityAdapter<Integration>();

const initialState = integrationsAdapter.getInitialState<{
  status: LoadingState;
  error: string | null;
}>({
  status: LoadingState.LOADING,
  error: null,
});

export const updateIntegration = createAsyncThunk<
  Integration,
  { id: string; changes: Partial<Integration> },
  { rejectValue: string }
>("integrations/updateIntegration", async ({ id, changes }, thunkAPI) => {
  try {
    const axios = await getAxiosInstanceWithAuth();
    const response = await axios.patch(`/integrations/${id}`, changes);

    return thunkAPI.fulfillWithValue(response.data);
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message);
  }
});

export const deleteIntegration = createAsyncThunk(
  "integrations/deleteIntegration",
  async (integrationId: string, thunkAPI) => {
    try {
      const axios = await getAxiosInstanceWithAuth();
      await axios.delete(`/integrations/${integrationId}`);

      return thunkAPI.fulfillWithValue(integrationId);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

const integrationsSlice = createSlice({
  name: "integrations",
  initialState,
  reducers: {
    setAllIntegrations: integrationsAdapter.setAll,
    upsertIntegration: integrationsAdapter.upsertOne,
  },
  extraReducers: (builder) => {
    builder
      .addCase(setAllIntegrations, (state) => {
        state.status = LoadingState.LOADED;
      })
      .addCase(updateIntegration.pending, (state, action) => {
        // Optimistic update
        integrationsAdapter.updateOne(state, {
          id: action.meta.arg.id,
          changes: action.meta.arg.changes,
        });

        state.status = LoadingState.LOADING;
      })
      .addCase(updateIntegration.fulfilled, (state, action) => {
        state.status = LoadingState.LOADED;

        integrationsAdapter.updateOne(state, {
          id: action.payload.id,
          changes: action.payload,
        });
      })
      .addCase(updateIntegration.rejected, (state, action) => {
        state.status = LoadingState.ERROR;
        state.error = action.error.message || null;
      })
      .addCase(deleteIntegration.pending, (state) => {
        state.status = LoadingState.LOADING;
      })
      .addCase(deleteIntegration.fulfilled, (state, action) => {
        state.status = LoadingState.LOADED;
        integrationsAdapter.removeOne(state, action.payload);
      })
      .addCase(deleteIntegration.rejected, (state, action) => {
        state.status = LoadingState.ERROR;
        state.error = action.error.message || null;
      });
  },
});

export const { setAllIntegrations, upsertIntegration } =
  integrationsSlice.actions;
export default integrationsSlice.reducer;

export const selectAllIntegrations: Selector<RootState, Integration[]> =
  createSelector(
    [(state: RootState) => state.integrations],
    (integrationsState) =>
      integrationsAdapter.getSelectors().selectAll(integrationsState),
  );

export const selectSlackIntegration: Selector<RootState, Integration | null> =
  createSelector(
    [selectAllIntegrations],
    (integrations) => integrations.find((i) => i.provider === "slack") || null,
  );

export const selectTeamsIntegration: Selector<RootState, Integration | null> =
  createSelector(
    [selectAllIntegrations],
    (integrations) =>
      integrations.find((i) => i.provider === IntegrationProvider.TEAMS) ||
      null,
  );

export const selectGreenhouseIntegration: Selector<
  RootState,
  Integration | null
> = createSelector(
  [selectAllIntegrations],
  (integrations) =>
    integrations.find((i) => i.provider === IntegrationProvider.GREENHOUSE) ||
    null,
);

export const selectFountainIntegration: Selector<
  RootState,
  Integration | null
> = createSelector(
  [selectAllIntegrations],
  (integrations) => integrations.find((i) => i.provider === "fountain") || null,
);
